import React, { useState, useEffect, useCallback } from 'react';
import { roomsAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Plus, Pencil, Trash2, Loader2, DoorOpen, Maximize2, Layers } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ROOM_TYPE_LABELS = {
  living_room: 'Living Room', bedroom: 'Bedroom', master_bedroom: 'Master Bedroom',
  kitchen: 'Kitchen', bathroom: 'Bathroom', dining_room: 'Dining Room',
  study: 'Study', office: 'Office', foyer: 'Foyer', hallway: 'Hallway',
  balcony: 'Balcony', terrace: 'Terrace', pooja_room: 'Pooja Room',
  utility: 'Utility', store_room: 'Store Room', guest_room: 'Guest Room',
  kids_room: "Kid's Room", walk_in_closet: 'Walk-in Closet',
  powder_room: 'Powder Room', media_room: 'Media Room', gym: 'Gym',
  pool_area: 'Pool Area', garden: 'Garden', garage: 'Garage', other: 'Other',
};

const ROOM_TYPES = Object.keys(ROOM_TYPE_LABELS);

export default function ProjectRooms({ projectId }) {
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [viewing, setViewing] = useState(null);

  const emptyForm = { name: '', room_type: 'other', floor: '', area_sqft: '', description: '' };
  const [form, setForm] = useState(emptyForm);

  const fetchRooms = useCallback(async () => {
    try {
      setLoading(true);
      const res = await roomsAPI.list(projectId);
      setRooms(res.data);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (editing) {
        const res = await roomsAPI.update(editing.room_id, form);
        setRooms(rooms.map(r => r.room_id === editing.room_id ? res.data : r));
      } else {
        const res = await roomsAPI.create(projectId, form);
        setRooms([...rooms, res.data]);
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm);
    } catch (err) {
      console.error('Failed to save room:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await roomsAPI.delete(deleteId);
      setRooms(rooms.filter(r => r.room_id !== deleteId));
      setDeleteId(null);
    } catch (err) {
      console.error('Failed to delete room:', err);
    }
  };

  const openEdit = (room) => {
    setEditing(room);
    setForm({
      name: room.name, room_type: room.room_type || 'other',
      floor: room.floor || '', area_sqft: room.area_sqft || '',
      description: room.description || '',
    });
    setDialogOpen(true);
  };

  const totalArea = rooms.reduce((sum, r) => sum + (r.area_sqft || 0), 0);

  if (loading) {
    return <div className="animate-pulse space-y-3"><div className="h-24 bg-muted rounded" /><div className="h-24 bg-muted rounded" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="project-rooms">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Rooms & Areas</h3>
          <p className="text-xs text-muted-foreground">{rooms.length} room{rooms.length !== 1 ? 's' : ''}{totalArea > 0 ? ` — ${totalArea.toLocaleString()} sq ft total` : ''}</p>
        </div>
        {canEdit && (
          <Button onClick={() => { setForm(emptyForm); setEditing(null); setDialogOpen(true); }} data-testid="add-room-btn">
            <Plus className="h-4 w-4 mr-2" />Add Room
          </Button>
        )}
      </div>

      {rooms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <DoorOpen className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-muted-foreground">No rooms added yet</p>
            <p className="text-xs text-muted-foreground mt-1">Break your project into rooms to organize FF&E, budgets, and tasks by area</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rooms.map((room) => (
            <Card
              key={room.room_id}
              className="group cursor-pointer hover:shadow-md transition-shadow"
              data-testid={`room-card-${room.room_id}`}
              onClick={() => setViewing(room)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') setViewing(room); }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-muted rounded">
                      <DoorOpen className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <h4 className="font-medium text-sm">{room.name}</h4>
                      <p className="text-xs text-muted-foreground capitalize">{ROOM_TYPE_LABELS[room.room_type] || room.room_type}</p>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openEdit(room); }} data-testid={`edit-room-${room.room_id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteId(room.room_id); }} data-testid={`delete-room-${room.room_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                  {room.area_sqft && (
                    <span className="flex items-center gap-1"><Maximize2 className="h-3 w-3" />{room.area_sqft} sq ft</span>
                  )}
                  {room.floor && (
                    <span className="flex items-center gap-1"><Layers className="h-3 w-3" />{room.floor}</span>
                  )}
                </div>
                {room.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{room.description}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Room' : 'Add Room'}</DialogTitle>
            <DialogDescription>Define a room or area within this project</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Room Name</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Master Bedroom" required data-testid="room-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.room_type} onValueChange={v => setForm({ ...form, room_type: v })}>
                    <SelectTrigger data-testid="room-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROOM_TYPES.map(t => <SelectItem key={t} value={t}>{ROOM_TYPE_LABELS[t]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Floor</Label>
                  <Input value={form.floor} onChange={e => setForm({ ...form, floor: e.target.value })} placeholder="e.g., Ground Floor" />
                </div>
                <div className="space-y-2">
                  <Label>Area (sq ft)</Label>
                  <Input type="number" step="0.01" value={form.area_sqft} onChange={e => setForm({ ...form, area_sqft: e.target.value })} placeholder="Optional" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Notes about this room..." />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="save-room-btn">
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editing ? 'Update' : 'Add Room'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Room</DialogTitle>
            <DialogDescription>Are you sure? This will remove the room from this project.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-room-btn">Delete Room</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Room Detail View */}
      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="room-detail-dialog">
          {viewing && <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className="p-2 bg-muted rounded"><DoorOpen className="h-4 w-4 text-muted-foreground" /></div>
                {viewing.name}
              </DialogTitle>
              <DialogDescription className="capitalize">{ROOM_TYPE_LABELS[viewing.room_type] || viewing.room_type}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {viewing.area_sqft && (
                  <div>
                    <div className="text-xs text-muted-foreground">Area</div>
                    <div className="flex items-center gap-1"><Maximize2 className="h-3.5 w-3.5" />{viewing.area_sqft} sq ft</div>
                  </div>
                )}
                {viewing.floor && (
                  <div>
                    <div className="text-xs text-muted-foreground">Floor</div>
                    <div className="flex items-center gap-1"><Layers className="h-3.5 w-3.5" />{viewing.floor}</div>
                  </div>
                )}
              </div>
              {viewing.description ? (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Description / Scope</div>
                  <div className="text-sm whitespace-pre-wrap p-3 rounded-md bg-muted/40 border" data-testid="room-detail-description">{viewing.description}</div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No description added yet.</p>
              )}
            </div>
            <DialogFooter className="flex-row gap-2">
              {canEdit && (
                <>
                  <Button variant="outline" onClick={() => { const r = viewing; setViewing(null); openEdit(r); }} data-testid="room-detail-edit-btn">
                    <Pencil className="h-4 w-4 mr-2" />Edit
                  </Button>
                  <Button variant="outline" className="text-destructive" onClick={() => { setDeleteId(viewing.room_id); setViewing(null); }}>
                    <Trash2 className="h-4 w-4 mr-2" />Delete
                  </Button>
                </>
              )}
              <Button onClick={() => setViewing(null)} className="ml-auto">Close</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
